/**
 * « À quelle demande cette proposition répond-elle ? » — reprise, chunk 9.AP.1.
 *
 * ## À quoi sert cet écran
 *
 * Le rattachement se pose maintenant au téléversement (9.AO.1). Restaient trois
 * cas qu'aucun geste ne réparait : les propositions versées avant ce chunk, celles
 * arrivées quand plusieurs demandes attendaient le même assureur — la machine
 * refuse désormais de choisir — et les rattachements simplement faux.
 *
 * ## Ce qu'il montre, et ce qu'il ne montre pas
 *
 * Une demande se reconnaît à son titre, à sa date d'envoi et à son état, jamais à
 * son identifiant : un UUID ne dit rien à personne, et le conseiller doit pouvoir
 * choisir sur ce qu'il connaît du dossier.
 *
 * ## Ce qu'il ne fait jamais
 *
 * Choisir à la place du conseiller. Cet écran n'a **aucune pré-sélection** : à la
 * différence du téléversement, où l'assureur vient d'être désigné et où un unique
 * créneau ouvert lève l'ambiguïté, une reprise arrive après coup, souvent parce
 * que quelque chose s'est mal passé. On y demande, on n'y devine pas.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiException } from "../../../lib/api";
import { getApiErrorMessage, normalizeLang } from "../../../lib/apiErrorMessage";
import { useInsurerLoop } from "../../../lib/queries/deadlines";
import { useUpdateRecipientLink, type SubmissionRow } from "../../../lib/queries/submissions";
import { creneauxDisponibles } from "../../projects/_parts/RecipientSlotPicker";

export function RecipientLinkSection({ submission }: { submission: SubmissionRow }) {
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.resolvedLanguage);
  const boucle = useInsurerLoop(submission.project_id);
  const majLien = useUpdateRecipientLink(submission.project_id);

  const lienCourant = submission.responds_to_recipient_id;
  const [choix, setChoix] = useState<string>("");
  const [erreur, setErreur] = useState<string | null>(null);

  const options = creneauxDisponibles(boucle.data, submission.insurer_code, lienCourant);
  const courant = options.find((o) => o.est_courant) ?? null;

  const dateFmt = new Intl.DateTimeFormat(i18n.resolvedLanguage ?? "fr", { dateStyle: "medium" });
  const libelle = (o: (typeof options)[number]) => {
    const titre = o.request_title?.trim() || t("submissions.link.untitled");
    const envoye = o.request_sent_at ? ` — ${dateFmt.format(new Date(o.request_sent_at))}` : "";
    const consigne = o.deja_consigne ? ` — ${t("submissions.link.already_recorded")}` : "";
    return `${titre}${envoye}${consigne}`;
  };

  const appliquer = async (recipientId: string | null) => {
    setErreur(null);
    try {
      await majLien.mutateAsync({ submissionId: submission.id, recipientId });
      setChoix("");
    } catch (err) {
      // Le message de l'API porte la raison exacte du refus — créneau pris,
      // assureur différent, demande close. La remplacer par une phrase générique
      // ferait perdre au conseiller la seule information qui l'aide.
      setErreur(
        err instanceof ApiException
          ? getApiErrorMessage(err, lang) || t("errors.generic")
          : t("errors.generic"),
      );
    }
  };

  // Les créneaux offerts SANS celui d'aujourd'hui : on ne propose pas de
  // « déplacer » une proposition vers l'endroit où elle est déjà.
  const autres = options.filter((o) => !o.est_courant);

  return (
    <section
      className="rounded-xl border border-slate-200 bg-white shadow-sm"
      aria-labelledby="recipient-link-title"
    >
      <header className="border-b border-slate-100 px-5 py-3.5">
        <h2 id="recipient-link-title" className="text-base font-semibold text-slate-900">
          {t("submissions.link.title")}
        </h2>
      </header>

      <div className="space-y-4 px-5 py-5">
        {boucle.isLoading ? (
          <p className="text-sm text-slate-500">{t("submissions.link.loading")}</p>
        ) : (
          <>
            {/* L'état courant, dit en toutes lettres. */}
            {courant ? (
              <p className="text-sm text-slate-800">
                {t("submissions.link.current")}{" "}
                <span className="font-medium">{libelle(courant)}</span>
              </p>
            ) : lienCourant ? (
              // Rattachée à un créneau que la boucle ne montre pas — demande
              // annulée, ou destinataire retiré. On le dit plutôt que d'afficher
              // « non rattachée », qui serait faux.
              <p className="text-sm text-amber-800">{t("submissions.link.current_unknown")}</p>
            ) : (
              <p className="text-sm font-medium text-amber-800">
                {t(
                  submission.quote_request_id
                    ? "submissions.link.legacy_to_confirm"
                    : "submissions.link.to_confirm",
                )}
              </p>
            )}

            {autres.length > 0 ? (
              <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                <div className="min-w-0 flex-1">
                  <label htmlFor="recipient_link" className="label">
                    {lienCourant
                      ? t("submissions.link.move_label")
                      : t("submissions.link.attach_label")}
                  </label>
                  <select
                    id="recipient_link"
                    value={choix}
                    onChange={(e) => setChoix(e.target.value)}
                    disabled={majLien.isPending}
                    className="input"
                  >
                    <option value="">— {t("submissions.link.choose")} —</option>
                    {autres.map((o) => (
                      <option key={o.recipient_id} value={o.recipient_id}>
                        {libelle(o)}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  type="button"
                  onClick={() => void appliquer(choix)}
                  disabled={!choix || majLien.isPending}
                  className="bg-brand-600 hover:bg-brand-700 focus-visible:ring-brand-500 touch-manipulation rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300"
                >
                  {majLien.isPending ? t("app.loading") : t("submissions.link.save")}
                </button>
              </div>
            ) : (
              !lienCourant && (
                // Aucun créneau compatible : c'est un fait, pas une anomalie. Les
                // propositions versées avant que le lien existe sont dans ce cas,
                // et rien ne sera inventé pour elles.
                <p className="text-sm text-slate-600">{t("submissions.link.none_available")}</p>
              )
            )}

            {lienCourant && (
              <div>
                <button
                  type="button"
                  onClick={() => void appliquer(null)}
                  disabled={majLien.isPending}
                  className="focus-visible:ring-brand-500 touch-manipulation rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {t("submissions.link.detach")}
                </button>
                {/* Détacher rend le créneau à l'attente : l'assureur redevient
                    relançable. Le conseiller doit le savoir avant de cliquer. */}
                <p className="mt-1 text-xs text-slate-500">{t("submissions.link.detach_hint")}</p>
              </div>
            )}

            <div aria-live="polite">
              {erreur && (
                <p
                  role="alert"
                  className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
                >
                  {erreur}
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </section>
  );
}
